import React, { type DetailedHTMLProps, type HTMLAttributes, type PropsWithChildren, useContext, useEffect, useId } from "react";
import { createPortal } from "react-dom";
import { twMerge } from "tailwind-merge";

import { usePortalRoot } from "../../../hooks/usePortalRoot";
import { DialogContext } from "../../../components/UI/Dialog/Dialog.context";

export interface DrawerProps extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  open?: boolean;
  unmount?: boolean;
  orientation: "top" | "bottom" | "left" | "right";
  onClose?: () => void;
}

const styles = {
  left: "-translate-x-full",
  right: "translate-x-full",
  top: "-translate-y-full",
  bottom: "translate-y-full",
};

export function SupDrawerBody({ children, ...props }: PropsWithChildren<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>>) {
  return (
    <div {...props} className={twMerge("flex-1 min-h-0 overflow-y-auto custom-scrollbar", props.className)}>
      {children}
    </div>
  );
}

export function SupDrawerFooter({ children, ...props }: PropsWithChildren<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>>) {
  return (
    <div {...props} className={twMerge("shrink-0", props.className)}>
      {children}
    </div>
  );
}

export function SupDrawer({ children, open = false, unmount = false, className, orientation = "bottom", ...restProps }: DrawerProps) {
  const modalId = useId();
  const { updateDialog, removeDialog } = useContext(DialogContext);
  const portalRoot = usePortalRoot(open, { unmountOnClose: false });

  useEffect(
    () => () => {
      removeDialog(modalId);
    },
    [modalId]
  );

  useEffect(() => {
    updateDialog(modalId, open);
  }, [modalId, open, updateDialog]);

  return portalRoot
    ? createPortal(
        <div
          {...restProps}
          className={twMerge(
            "sup-drawer flex flex-col fixed inset-0 z-50 bg-neutral transition-transform ease-in-out duration-500",
            open ? "translate-x-0 translate-y-0" : styles[orientation],
            className
          )}
        >
          {open || !unmount ? children : null}
        </div>,
        portalRoot
      )
    : null;
}
